import * as yup from "yup";
import YupPassword from "yup-password";
import { initValidation } from "./Validation";

YupPassword(yup);

const signInSchema = initValidation({
  email: yup.string().email().required(),
  password: yup.string().required(),
});

const forgotPasswordSchema = initValidation({
  email: yup.string().email().required(),
});

const confirmPasswordSchema = initValidation({
  password: yup
    .string()
    .required()
    .min(
      8,
      "Password must contain 8 or more characters with at least one of each: uppercase, lowercase, number and special",
    )
    .minLowercase(1, "Password must contain at least 1 lower case letter")
    .minUppercase(1, "Password must contain at least 1 upper case letter")
    .minNumbers(1, "Password must contain at least 1 number")
    .minSymbols(1, "Password must contain at least 1 special character"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required(),
});

const baseAddEditUserSchema = {
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  phoneNumber: yup
    .string()
    .test("phone-format", "Phone number must start with + and include country code", value => {
      if (!value || value.trim() === "") return false; // Required field
      // Check if it starts with + and has reasonable length for international number
      return value.startsWith("+") && value.replace(/[^\d]/g, "").length >= 10;
    })
    .required(),
  role: yup.object().required(),
};

const addUserSchema = initValidation({
  ...baseAddEditUserSchema,
  email: yup.string().email().required(),
});

const editUserSchema = initValidation({
  ...baseAddEditUserSchema,
});

const addAdminSchema = initValidation({
  ...baseAddEditUserSchema,
  email: yup.string().email().required(),
  role: yup.string().required("Please select a role"),
});

const editAdminSchema = initValidation({
  ...baseAddEditUserSchema,
  role: yup.string().required("Please select a role"),
});

const changeEmailSchema = initValidation({
  email: yup.string().email().required(),
});

const vehicleSchema = initValidation({
  tag: yup.string().required(),
  name: yup.string().required(),
  initialMileage: yup.string().required(),
  mileage: yup.string().required(),
});

const workOrderSchema = initValidation({
  startTime: yup.string().required(),
  endTime: yup.string().required(),
  vehicleIds: yup.object(),
  techIds: yup.array().required(),
  contactIds: yup.array(),
  equipmentIds: yup.array(),
});

const contactSchema = initValidation({
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  jobTitle: yup.string().required(),
  phoneExtension: yup.string(),
  phone: yup
    .string()
    .test("phone-format", "Phone number must start with + and include country code", value => {
      if (!value || value.trim() === "") return true; // Optional field
      return value.startsWith("+") && value.replace(/[^\d]/g, "").length >= 10;
    }),
  mobilePhone: yup
    .string()
    .test(
      "phone-format",
      "Mobile phone number must start with + and include country code",
      value => {
        if (!value || value.trim() === "") return true; // Optional field
        return value.startsWith("+") && value.replace(/[^\d]/g, "").length >= 10;
      },
    ),
  email: yup.string().email(),
  details: yup.string().notRequired(),
});

const companySchema = initValidation({
  name: yup.string().required(),
  email: yup
    .string()
    .test("email-format", "Please enter a valid email address", value => {
      if (!value || value.trim() === "") return true; // Optional field
      return yup.string().email().isValidSync(value);
    })
    .optional(),
  mobilePhone: yup
    .string()
    .test(
      "phone-format",
      "Mobile phone number must start with + and include country code",
      value => {
        if (!value || value.trim() === "") return true; // Optional field
        // Check if it starts with + and has reasonable length for international number
        return value.startsWith("+") && value.replace(/[^\d]/g, "").length >= 10;
      },
    )
    .nullable(),
  phone: yup
    .string()
    .test(
      "phone-format",
      "Office phone number must start with + and include country code",
      value => {
        if (!value || value.trim() === "") return true; // Optional field
        // Check if it starts with + and has reasonable length for international number
        return value.startsWith("+") && value.replace(/[^\d]/g, "").length >= 10;
      },
    )
    .nullable(),
  phoneExtension: yup.string().optional().nullable(),
  primaryContact: yup
    .object({
      firstName: yup.string().optional(),
      lastName: yup.string().optional(),
      jobTitle: yup.string().optional(),
    })
    .optional(),
  shippingAddress: yup
    .object({
      address: yup.string().optional(),
      city: yup.string().optional(),
      country: yup.string().optional(),
      state: yup.string().optional(),
      zip: yup.string().optional(),
    })
    .optional(),
  billingAddress: yup
    .object({
      address: yup.string().optional(),
      city: yup.string().optional(),
      country: yup.string().optional(),
      state: yup.string().optional(),
      zip: yup.string().optional(),
    })
    .optional(),
  companyNotes: yup.string().optional().nullable(),
});

const notificationSchema = initValidation({
  subject: yup.string().required(),
  message: yup.string().required(),
});

const rescheduleSchema = initValidation({
  startTime: yup.string().required(),
  endTime: yup.string().required(),
});

export {
  signInSchema,
  forgotPasswordSchema,
  addUserSchema,
  editUserSchema,
  confirmPasswordSchema,
  changeEmailSchema,
  vehicleSchema,
  workOrderSchema,
  contactSchema,
  rescheduleSchema,
  notificationSchema,
  addAdminSchema,
  editAdminSchema,
  companySchema,
};
